import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { CollectionImage } from "@/components/collections/CollectionImage";
import { featuredCollections, imageSrc } from "@/lib/catalog";
import { formatPrice } from "@/lib/format";
import { getJson } from "@/lib/publicApi";
import { useReducedMotion } from "./motion";

// Hero simples: fotografias reais a toda a largura e altura, a alternar devagar entre conjuntos, a encomenda e bebés com as peças.
const cat = (id) => ({ src: `/img/catalog/${id}-1200.webp`, small: `/img/catalog/${id}-400.webp`, wide: 1200 });
const hero = (name) => ({ src: `/img/hero/${name}.webp`, small: `/img/hero/${name}-800.webp`, wide: 1400 });
// O porta-chupetas personalizado abre o hero: as fotografias são verticais, com a peça à direita, e o texto fica à esquerda.
const BASE_SCENES = [
  { ...cat("pc1"), pos: "88% 45%", alt: "Porta-chupetas ursinho Babybib de vichy azul com o nome Afonso" },
  { ...hero("natal"), pos: "88% 10%", alt: "Bebé a sorrir com um laço de veludo bordeaux, a anunciar a coleção de Natal Babybib", natal: true },
  { ...cat("cj1"), pos: "50% 50%", alt: "Muda-fraldas e porta-chupetas Babybib em tecido de florzinhas" },
  { ...hero("bebe-1"), pos: "50% 40%", alt: "Bebé a sorrir com um babete Babybib de vichy rosa" },
  { ...hero("encomendas"), pos: "50% 74%", alt: "Encomenda Babybib: caixa aberta com um saquinho de cetim e o cartão do ursinho" },
  { ...hero("bebe-4"), pos: "50% 52%", alt: "Bebé sentada com um babete Babybib e um porta-chupetas" },
];

// Fim do hero em ondinhas, que descem sobre a secção seguinte; um pesponto tracejado acompanha a onda, por dentro.
const enc = (svg) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
const WAVE = enc('<svg xmlns="http://www.w3.org/2000/svg" width="60" height="30" viewBox="0 0 60 30"><path d="M0 0 H60 V12 Q45 1 30 12 T0 12 Z" fill="#000"/></svg>');
const HERO_MASK = `linear-gradient(#000 0 0) 0 0 / 100% calc(100% - 30px) no-repeat, ${WAVE} 0 100% / 60px 30px repeat-x`;
const STITCH = enc('<svg xmlns="http://www.w3.org/2000/svg" width="60" height="30" viewBox="0 0 60 30"><path d="M0 12 Q15 23 30 12 T60 12" transform="translate(0 -5)" fill="none" stroke="rgba(255,255,255,.8)" stroke-width="2" pathLength="60" stroke-dasharray="5 5"/></svg>');

// Campanhas com código (criadas no painel, em Cupões): entram a seguir à primeira fotografia, com a imagem da campanha e o código.
const campaignScene = (c) => ({ src: imageSrc(c.imageId, 1200), small: imageSrc(c.imageId, 400), wide: 1200, pos: "50% 50%", alt: c.title, campaign: c });

// Coleções em destaque no carrossel (definidas no painel, em Coleções): entram a seguir à primeira fotografia, com a imagem da coleção.
const collectionScene = (c) => ({ src: `col-${c.slug}`, alt: c.feature.title, collection: c });

function CollectionBlock({ c }) {
  return (
    <div className="bb-container relative z-10 flex h-full flex-col justify-end pb-[calc(2.5rem+30px)] sm:pb-[calc(3.5rem+30px)] lg:justify-center lg:pb-[30px] lg:pr-[50%]" data-testid={`hero-collection-slide-${c.slug}`}>
      <p className="bb-eyebrow mb-3 !text-ivory">{c.label}</p>
      <h2 className="bb-h1 max-w-[16ch] text-ivory [text-shadow:0_1px_20px_rgba(30,24,20,.35)] sm:max-w-[20ch]" style={{ fontSize: "clamp(2rem, 5.2vw, 4.4rem)", lineHeight: 1.04 }}>{c.feature.title}</h2>
      {c.feature.text && <p className="mt-2 max-w-md text-[14px] leading-snug text-ivory/95 [text-shadow:0_1px_10px_rgba(30,24,20,.4)] sm:mt-3 sm:text-[17px]">{c.feature.text}</p>}
      <div className="mt-4 sm:mt-6">
        <Link to={`/colecoes/${c.slug}`} data-testid={`hero-collection-button-${c.slug}`} className="bb-btn-light group inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap px-[20px] text-[13px] font-semibold sm:min-h-[52px] sm:gap-2 sm:px-[30px] sm:text-[15px]">
          {c.feature.buttonLabel} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 sm:h-4 sm:w-4" />
        </Link>
      </div>
    </div>
  );
}

// A última palavra do título (ex.: "miminho") sai em rosa e na letra de destaque, como no título inicial do hero.
const titleWords = (t) => {
  const i = t.trim().lastIndexOf(" ");
  return i < 0 ? t : <>{t.slice(0, i + 1)}<em className="bb-accent text-[#F6CFCB]">{t.slice(i + 1)}</em></>;
};

// Anúncio da coleção de Natal (sem botão: ainda não está à venda).
function NatalBlock() {
  return (
    <div className="bb-container relative z-10 flex h-full flex-col justify-end pb-[calc(2.5rem+30px)] sm:pb-[calc(3.5rem+30px)] lg:justify-center lg:pb-[30px] lg:pr-[50%]" data-testid="hero-natal">
      <p className="bb-eyebrow mb-3 !text-ivory">Coleção de Natal</p>
      <h2 className="bb-h1 max-w-[16ch] text-ivory [text-shadow:0_1px_20px_rgba(30,24,20,.35)] sm:max-w-[20ch]" style={{ fontSize: "clamp(2rem, 5.2vw, 4.4rem)", lineHeight: 1.04 }}>
        O Natal chega <em className="bb-accent text-[#F6CFCB]">em breve.</em>
      </h2>
      <p className="mt-2 max-w-md text-[14px] leading-snug text-ivory/95 [text-shadow:0_1px_10px_rgba(30,24,20,.4)] sm:mt-3 sm:text-[17px]">Ideias para os teus presentes de Natal em breve.</p>
    </div>
  );
}

function CampaignBlock({ c }) {
  const navigate = useNavigate();
  const use = () => {
    try { localStorage.setItem("babybib-coupon", c.code); } catch { /* sem armazenamento */ }
    toast.success(`Código ${c.code} guardado`, { description: "Aplica-se no checkout." });
    navigate("/loja");
  };
  return (
    <div className="bb-container relative z-10 flex h-full flex-col justify-end pb-[calc(2.5rem+30px)] sm:pb-[calc(3.5rem+30px)] lg:justify-center lg:pb-[30px] lg:pr-[50%]" data-testid="hero-campaign">
      <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-ivory/90 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-bordeaux backdrop-blur" data-testid="hero-campaign-code"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Código {c.code}</span>
      <h2 className="bb-h1 max-w-[16ch] text-ivory [text-shadow:0_1px_20px_rgba(30,24,20,.35)] sm:max-w-[20ch]" style={{ fontSize: "clamp(2rem, 5.2vw, 4.4rem)", lineHeight: 1.04 }}>{titleWords(c.title)}</h2>
      {c.text && <p className="mt-2 max-w-md text-[14px] leading-snug text-ivory/95 [text-shadow:0_1px_10px_rgba(30,24,20,.4)] sm:mt-3 sm:text-[17px]">{c.text}</p>}
      {c.minOrder && <p className="mt-1 text-xs text-ivory/85">Em compras a partir de {formatPrice(c.minOrder / 100)}.</p>}
      <div className="mt-4 sm:mt-6">
        <button type="button" onClick={use} data-testid="hero-campaign-button" className="bb-btn-light group min-h-[44px] gap-1.5 whitespace-nowrap px-[20px] text-[13px] font-semibold sm:min-h-[52px] sm:gap-2 sm:px-[30px] sm:text-[15px]">
          {c.buttonLabel} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 sm:h-4 sm:w-4" />
        </button>
      </div>
    </div>
  );
}

export function HeroEditorial() {
  const reduced = useReducedMotion();
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    getJson("/coupons/campaigns").then((l) => Array.isArray(l) && setCampaigns(l)).catch(() => null);
  }, []);
  const collections = featuredCollections("carousel").filter((c) => c.feature.image?.id);
  const SCENES = [BASE_SCENES[0], ...collections.map(collectionScene), ...campaigns.map(campaignScene), ...BASE_SCENES.slice(1)];
  const [i, setI] = useState(0);
  const [hold, setHold] = useState(false);
  const touchX = useRef(null);
  const go = (d) => setI((n) => (n + d + SCENES.length) % SCENES.length);

  useEffect(() => {
    if (hold || reduced) return undefined;
    const t = setTimeout(() => setI((n) => (n + 1) % SCENES.length), 6000);
    return () => clearTimeout(t);
  }, [i, hold, reduced, SCENES.length]);

  return (
    <section
      onMouseEnter={() => setHold(true)}
      onMouseLeave={() => setHold(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        // telemóvel: só deslizar (sem botões); um arrasto horizontal muda de fotografia
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
      className="relative isolate z-[1] h-[calc(min(76svh,760px)+30px)] min-h-[500px] overflow-hidden bg-[#3d332d] text-ivory"
      style={{ WebkitMask: HERO_MASK, mask: HERO_MASK }}
      data-testid="home-hero"
      aria-label="Apresentação"
    >
      {SCENES.map((s, k) => {
        const motion = { opacity: k === i ? 1 : 0, transform: k === i && !reduced ? "scale(1.04)" : "scale(1)", transition: reduced ? "none" : `opacity 1.4s ease, transform ${k === i ? 12 : 1.4}s ease-out` };
        return s.collection ? (
          <div key={s.src} aria-hidden={k !== i} className="absolute inset-0" style={motion}>
            <CollectionImage image={s.collection.feature.image} alt={k === i ? s.alt : ""} eager={k === 0} className="h-full w-full object-cover" />
          </div>
        ) : (
          <img
            key={s.src}
            src={s.src}
            srcSet={`${s.small} 400w, ${s.src} ${s.wide}w`}
            sizes="100vw"
            alt={k === i ? s.alt : ""}
            aria-hidden={k !== i}
            fetchPriority={k === 0 ? "high" : "auto"}
            loading={k === 0 ? "eager" : "lazy"}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: s.pos, ...motion }}
          />
        );
      })}
      <span aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(90deg,rgba(43,36,32,.66)_0%,rgba(43,36,32,.3)_50%,rgba(43,36,32,.05)_100%),linear-gradient(0deg,rgba(43,36,32,.5)_0%,rgba(43,36,32,0)_55%)]" />
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[rgba(43,36,32,.5)] to-transparent" />
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-[30px]" style={{ backgroundImage: STITCH, backgroundSize: "60px 30px", backgroundRepeat: "repeat-x" }} />

      <button type="button" onClick={() => go(-1)} aria-label="Fotografia anterior" data-testid="hero-prev" className="absolute left-2 lg:left-5 top-1/2 z-20 hidden h-11 w-11 -translate-y-[calc(50%+15px)] place-items-center rounded-full text-ivory/60 transition-colors hover:bg-ivory/15 hover:text-ivory sm:grid">
        <ChevronLeft className="h-7 w-7" strokeWidth={1.4} aria-hidden="true" />
      </button>
      <button type="button" onClick={() => go(1)} aria-label="Fotografia seguinte" data-testid="hero-next" className="absolute right-2 lg:right-5 top-1/2 z-20 hidden h-11 w-11 -translate-y-[calc(50%+15px)] place-items-center rounded-full text-ivory/60 transition-colors hover:bg-ivory/15 hover:text-ivory sm:grid">
        <ChevronRight className="h-7 w-7" strokeWidth={1.4} aria-hidden="true" />
      </button>

      {SCENES[i % SCENES.length].natal ? <NatalBlock /> : SCENES[i % SCENES.length].collection ? <CollectionBlock c={SCENES[i % SCENES.length].collection} /> : SCENES[i % SCENES.length].campaign ? <CampaignBlock c={SCENES[i % SCENES.length].campaign} /> : (
      <div className="bb-container relative z-10 flex h-full flex-col justify-end pb-[calc(2.5rem+30px)] sm:pb-[calc(3.5rem+30px)] lg:justify-center lg:pb-[30px] lg:pr-[50%]">
        <h1 className="bb-h1 max-w-[16ch] text-ivory [text-shadow:0_1px_20px_rgba(30,24,20,.35)] sm:max-w-[20ch]" style={{ fontSize: "clamp(2rem, 5.2vw, 4.4rem)", lineHeight: 1.04 }}>
          Pequeninas peças, <em className="bb-accent text-[#F6CFCB]">feitas com muito amor.</em>
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-2.5 sm:mt-7 sm:gap-3">
          <Link to="/produto/porta-chupetas#personalizar" data-testid="hero-personalize-button" className="bb-btn-light group min-h-[44px] gap-1.5 whitespace-nowrap px-[20px] text-[13px] font-semibold sm:min-h-[52px] sm:gap-2 sm:px-[30px] sm:text-[15px]">
            Personalizar o meu porta-chupetas <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 sm:h-4 sm:w-4" />
          </Link>
          <Link to="/loja" data-testid="hero-explore-button" className="inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border border-ivory/60 px-[18px] text-[13px] text-ivory transition-colors hover:bg-ivory/15 sm:min-h-[52px] sm:px-[26px] sm:text-[15px]">
            Descobrir a coleção
          </Link>
        </div>
      </div>
      )}
    </section>
  );
}
